import { FC, useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { Icon, IconColor, IconSize, IconTypes } from './Icon'
import { useTranslation } from 'react-i18next'

type TextareaProps = {
  className?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  readonly?: boolean;
  rows?: number;
  maxLength?: number;
  value: string;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  backgroundColor?: string;
  containerMargin?: string;
  labelColor?: string;
  borderColor?: string;
  onAIClick?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  isAIGenerating?: boolean;
  showAIButton?: boolean;
  aiButtonLabel?: string;
  /** Icon-only AI control (circular, bottom-right) */
  aiButtonIconOnly?: boolean;
  /** Compact pill with sparkle + label (competition description) */
  aiButtonPill?: boolean;
  showGifButton?: boolean;
  onGifClick?: () => void;
}

const Textarea: FC<TextareaProps> = ({
  className,
  label,
  placeholder,
  required = false,
  readonly = false,
  rows = 10,
  maxLength = 524288,
  value: textareaValue,
  onChange,
  onFocus,
  onBlur,
  backgroundColor,
  containerMargin,
  labelColor,
  borderColor,
  onAIClick,
  isAIGenerating = false,
  showAIButton = false,
  aiButtonLabel = 'AI Analyse and Predict',
  aiButtonIconOnly = false,
  aiButtonPill = false,
  showGifButton = false,
  onGifClick,
}) => {
  const { t } = useTranslation()
  const [ cursor, setCursor ] = useState(null)
  const [ isFocused, setIsFocused ] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    ref?.current?.setSelectionRange(cursor, cursor)
  }, [ ref, cursor, textareaValue ])

  const onChangeTextarea = (event) => {
    setCursor(event?.target?.selectionStart)
    onChange(event?.target?.value || '')
  }

  const handleFocus = () => {
    setIsFocused(true)
    onFocus?.()
  }

  const handleBlur = () => {
    setIsFocused(false)
    onBlur?.()
  }

  return (
    <StyledContainer className={className} containerMargin={containerMargin}>
      {label ? (
        <StyledLabel required={required} labelColor={labelColor}>
          {label}
        </StyledLabel>
      ) : null}
      <ContainerInput>
        <StyledTextarea
          ref={ref}
          placeholder={placeholder}
          value={textareaValue}
          readOnly={readonly}
          maxLength={maxLength}
          rows={rows}
          onChange={onChangeTextarea}
          onFocus={handleFocus}
          onBlur={handleBlur}
          backgroundColor={backgroundColor}
          borderColor={borderColor}
          $hasAIButton={showAIButton}
          $hasGifButton={showGifButton}
          $isHidden={isAIGenerating}
        />
        {isAIGenerating && (
          <AIProcessingOverlay>
            <AIProcessingContent>
              <AIIconContainer>
                <Icon type={IconTypes.starsIcon} width={40} height={40} color={IconColor.violete600} />
              </AIIconContainer>
              <AIProcessingText>{t('ui.aiProcessing')}</AIProcessingText>
            </AIProcessingContent>
          </AIProcessingOverlay>
        )}
        {showAIButton && onAIClick && !isAIGenerating && (
          <AIButton
            type="button"
            $iconOnly={aiButtonIconOnly && !aiButtonPill}
            $pill={aiButtonPill}
            onClick={onAIClick}
            disabled={isAIGenerating}
          >
            <IconWrapper $isAnimating={isAIGenerating}>
              <Icon
                type={IconTypes.starsIcon}
                width={aiButtonPill ? 16 : aiButtonIconOnly ? 18 : 17}
                height={aiButtonPill ? 16 : aiButtonIconOnly ? 18 : 18}
                color={IconColor.violete600}
              />
            </IconWrapper>
            {(aiButtonPill || !aiButtonIconOnly) && aiButtonLabel ? (
              <span>{aiButtonLabel}</span>
            ) : null}
          </AIButton>
        )}
        {showGifButton && onGifClick && (
          <GifButton onClick={onGifClick}>
            <Icon
              type={IconTypes.addGif}
              color={IconColor.viloet400}
              height={20}
              width={20}
            />
            <span>{t('channels.insertGif')}</span>
          </GifButton>
        )}
      </ContainerInput>
    </StyledContainer>
  )
}

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.15);
    opacity: 0.8;
  }
`

const dotAnimation = keyframes`
  0%, 20% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`

const IconWrapper = styled.div<{ $isAnimating: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--button-primary-color);
  animation: ${props => props.$isAnimating ? pulse : 'none'} 1.5s ease-in-out infinite;
`

const LoadingDots = styled.span`
  display: inline-flex;
  margin-left: 2px;
`

const Dot = styled.span<{ delay: number }>`
  animation: ${dotAnimation} 1.4s infinite;
  animation-delay: ${props => props.delay}s;
  font-weight: bold;
`

const AIButton = styled.button<{ $iconOnly?: boolean; $pill?: boolean }>`
  position: absolute;
  bottom: ${({ $pill }) => ($pill ? '10px' : '12px')};
  right: ${({ $pill }) => ($pill ? '10px' : '12px')};
  display: flex;
  align-items: center;
  gap: ${({ $pill }) => ($pill ? '6px' : '8px')};
  background: ${({ $iconOnly, $pill }) => {
    if ($pill) return 'var(--zinc-800, #27272a)'
    if ($iconOnly) return 'var(--zinc-700, #3f3f46)'
    return 'transparent'
  }};
  border: ${({ $iconOnly, $pill }) =>
    $iconOnly && !$pill ? '1px solid var(--zinc-600, #52525b)' : 'none'};
  padding: ${({ $iconOnly, $pill }) => {
    if ($pill) return '0 12px'
    if ($iconOnly) return '0'
    return '8px 12px'
  }};
  width: ${({ $iconOnly, $pill }) => ($iconOnly && !$pill ? '32px' : 'auto')};
  height: ${({ $iconOnly, $pill }) => {
    if ($pill) return '36px'
    if ($iconOnly && !$pill) return '32px'
    return 'auto'
  }};
  border-radius: ${({ $iconOnly, $pill }) => {
    if ($pill) return '8px'
    if ($iconOnly) return '9999px'
    return '0'
  }};
  box-shadow: ${({ $iconOnly, $pill }) =>
    $iconOnly && !$pill ? '0 2px 8px rgba(0, 0, 0, 0.2)' : 'none'};
  justify-content: center;
  cursor: pointer;
  color: ${({ $pill }) => ($pill ? 'var(--zinc-100, #f4f4f5)' : 'var(--button-primary-color)')};
  font-size: ${({ $pill }) => ($pill ? '13px' : 'var(--font-size-small)')};
  font-weight: 600;
  transition: opacity 0.2s ease;
  z-index: 1;

  &:hover {
    opacity: 0.85;
  }

  &:active {
    opacity: 0.65;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  span {
    display: flex;
    align-items: center;
  }
`

const GifButton = styled.button`
  position: absolute;
  bottom: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  border: none;
  gap: 8px;
  background: transparent;
  cursor: pointer;
  z-index: 1;

  span {
    color: var(--button-primary-color);
    font-weight: 600;
    font-size: var(--font-size-small);
  }
`

const StyledContainer = styled.div<{containerMargin: string}>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`

const StyledLabel = styled.label<{required: boolean, labelColor: string}>`
  margin-bottom: 8px;
  color: var(--color-input-label-primary);
  line-height: var(--font-size-small);
  ${(props) => props.required && css`&:after {content: " *"; color: var(--color-gray-darker)}`}
`

const ContainerInput = styled.div`
  display: flex;
  position: relative;
  align-items: flex-start;
  width: 100%;
`

const StyledTextarea = styled.textarea<{
  backgroundColor: string
  borderColor: string
  $hasAIButton: boolean
  $hasGifButton: boolean
  $isHidden: boolean
}>`
  background: var(--color-input-background-primary);
  border: 1px solid var(--color-input-border-primary);
  color: var(--text-primary);
  border-radius: 12px;
  outline: none;
  padding: 14px;
  padding-bottom: ${props => (props.$hasAIButton || props.$hasGifButton) ? '50px' : '14px'};
  width: 100%;
  resize: none;
  font-size: var(--font-size-normal);
  font-family: var(--font-primary);
  opacity: ${props => props.$isHidden ? '0' : '1'};
  visibility: ${props => props.$isHidden ? 'hidden' : 'visible'};

  ::placeholder {
    line-height: 24px;
    color: var(--text-color-tertiary);
    font-size: var(--font-size-normal);
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--scrollbar-color);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--scrollbar-color);
    opacity: 0.8;
  }

  scrollbar-width: thin;
  scrollbar-color: var(--scrollbar-color) transparent;
`

const shine = keyframes`
  0% {
    transform: translateX(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(200%) rotate(45deg);
  }
`

const AIProcessingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(270deg, rgba(34, 34, 38, 0) 0%, var(--violet-900) 42.31%, rgba(34, 34, 38, 0) 100%);
  border-radius: 12px;
  border: 1px solid rgba(var(--violet-500-rgb), 0.3);
  overflow: hidden;
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    animation: ${shine} 2s infinite;
  }
`

const AIProcessingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  z-index: 1;
  position: relative;
`

const AIIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${pulse} 1.5s ease-in-out infinite;
`

const AIProcessingText = styled.p`
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  text-align: center;
`

export default Textarea